import * as React from "react";
import { useState } from "react";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import Navigation from "@/components/Navigation";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, Search, Heart, Share2, Sparkles } from "lucide-react";
import { Footer } from "@/components/Footer";
import { blogPosts, blogCategories, getFeaturedPost } from "@/data/blogContent";
import avtLogoFeatured from "@/assets/avt-logo-featured.jpg";

const Blog = () => {
  useScrollToTop();
  const { language } = useI18n();
  const seo = getSeoCopy("blog", language);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  const trendingTags = ["Smart Hygiene", "Wellness Tech", "Spa Bathroom", "Self-Care", "Modern Living"];

  // Map blog posts to include image category for rendering
  const allBlogPosts = blogPosts.map(post => ({
    ...post,
    image: post.category === "Wellness" ? "wellness" : 
           post.category === "Design" ? "design" : 
           post.category === "Technology" ? "spa" : 
           post.category === "Buying Guide" ? "design" :
           post.category === "Comparison" ? "spa" :
           post.category === "Product Review" ? "wellness" : "wellness"
  }));

  const filteredPosts = allBlogPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = getFeaturedPost() ? 
    { ...getFeaturedPost()!, image: "wellness" } : 
    allBlogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <SeoHead route="blog" />
      <Navigation />
      
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-accent/5 to-background py-20 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,hsl(var(--accent)/0.08),transparent_50%)]" />
        
        <div className="container mx-auto max-w-5xl text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Your Wellness Resource</span>
          </div>
          
          <h1 className="font-playfair text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight">
            {seo.h1}
          </h1>
          
          <p className="font-inter text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover how smart sanitary technology transforms daily comfort for the modern woman
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Featured Article Hero */}
        {featuredPost && (
          <div className="mb-16">
            <Card className="group overflow-hidden border-0 shadow-luxury hover:shadow-elegant transition-all duration-700 bg-gradient-to-br from-card via-card to-primary/5">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-80 md:h-auto overflow-hidden bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
                  <img
                    src={avtLogoFeatured}
                    alt={featuredPost.title}
                    className="w-full h-full object-contain p-12 group-hover:scale-105 transition-transform duration-700"
                    loading="eager"
                  />
                </div>
                
                <CardHeader className="p-8 md:p-12 flex flex-col justify-center">
                  <Badge className="w-fit mb-4 bg-primary/10 text-primary border-primary/30">Featured Article</Badge>
                  <CardTitle className="font-playfair text-3xl md:text-4xl mb-4 group-hover:text-primary transition-colors leading-tight">
                    {featuredPost.title}
                  </CardTitle>
                  <CardDescription className="text-base md:text-lg mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </CardDescription>
                  
                  <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {featuredPost.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </span>
                    <Badge variant="outline">{featuredPost.category}</Badge>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button asChild className="group/btn flex-1 md:flex-none">
                      <Link to={featuredPost.slug}>
                        Read Article
                        <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="icon" className="hover:bg-primary/10">
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="hover:bg-primary/10">
                      <Heart className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
              </div>
            </Card>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-12 space-y-6">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-base rounded-full border-border/50 focus:border-primary/50 shadow-soft"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3">
            {blogCategories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full transition-all duration-300 ${
                  selectedCategory === category 
                    ? "shadow-soft" 
                    : "hover:bg-primary/10 hover:border-primary/50"
                }`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Trending Tags */}
          <div className="flex flex-wrap justify-center gap-2 items-center">
            <span className="text-sm font-medium text-muted-foreground mr-2">Trending:</span>
            {trendingTags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="cursor-pointer hover:bg-primary/20 hover:text-primary transition-colors"
                onClick={() => setSearchQuery(tag)}
              >
                #{tag}
              </Badge>
            ))}
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {regularPosts.map((post, index) => (
            <Card 
              key={index} 
              className="group overflow-hidden border-border/50 hover:border-primary/30 hover:shadow-luxury transition-all duration-500 flex flex-col bg-card/80 backdrop-blur-sm"
            >
              {/* Card Image */}
              <div className="relative h-48 overflow-hidden bg-muted">
                <img
                  src={
                    post.image === "wellness" ? "/lovable-uploads/fb3e1c31-a595-428d-a806-f3b39bec2624.png" :
                    post.image === "spa" ? "/images/m132-lifestyle-setting.jpg" :
                    post.image === "design" ? "/images/m158-prime-product.jpg" :
                    post.image === "family" ? "/lovable-uploads/7a774286-73c5-43d4-b368-b47766cb1465.png" :
                    post.image === "hygiene" ? "/images/z240-elite-product.jpg" :
                    "/images/z300-future-product.jpg"
                  }
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>

              <CardHeader className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-xs">{post.category}</Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.readTime}
                  </span>
                </div>
                
                <CardTitle className="font-playfair text-xl mb-3 group-hover:text-primary transition-colors leading-tight">
                  {post.title}
                </CardTitle>
                
                <CardDescription className="text-sm leading-relaxed line-clamp-3">
                  {post.excerpt}
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {post.date}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-primary/10">
                      <Share2 className="w-3 h-3" />
                    </Button>
                    <Button asChild variant="ghost" size="sm" className="group/btn px-3">
                      <Link to={post.slug}>
                        Read
                        <ArrowRight className="w-3 h-3 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Newsletter Signup */}
        <Card className="max-w-4xl mx-auto overflow-hidden border-0 shadow-luxury bg-gradient-to-br from-primary/5 via-card to-accent/5">
          <CardHeader className="text-center pb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 mx-auto">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="font-playfair text-3xl mb-3">
              Join Our Wellness Community
            </CardTitle>
            <CardDescription className="text-base">
              Receive expert insights, wellness tips, and exclusive updates directly to your inbox
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1 h-12 border-border/50 focus:border-primary/50"
              />
              <Button type="submit" className="h-12 px-8 whitespace-nowrap">
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
            <p className="text-xs text-center text-muted-foreground mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default Blog;
